import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay } from 'rxjs';

import { PrivaNotificationModule } from '@priva/components/notification';
import { PrivaLocalizationModule } from '@priva/localization';

import { IosContextMenuDirective } from 'app/shared/features/map-row-selector/directive/ios-context-menu.directive';

import { defaultActivity } from '../../../activity-selector/models';
import { GreenHouseMap } from '../../models';
import { MapRowComponent } from '../map-row/map-row.component';
import { MapRowListStore } from './map-row-list.store';
import { MapRowPositionPipe } from './map-row-position.pipe';

@Component({
    selector: 'app-map-row-list',
    templateUrl: './map-row-list.component.html',
    styleUrl: './map-row-list.component.scss',
    imports: [
        AsyncPipe,
        ScrollingModule,
        PrivaLocalizationModule,
        PrivaNotificationModule,
        IosContextMenuDirective,
        MapRowPositionPipe,
        MapRowComponent,
    ],
    providers: [MapRowListStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapRowListComponent {
    private componentStore = inject(MapRowListStore);

    activity = input(defaultActivity());

    @ViewChild('viewport')
    viewport!: CdkVirtualScrollViewport;

    map$ = this.componentStore.map$;

    constructor() {
        this.componentStore.state$
            .pipe(
                takeUntilDestroyed(),
                // delay is required to let scroll happen after map rows are updated in template
                delay(1),
            )
            .subscribe(({ map, filteredRows, recentRowNumber }) => {
                this.scrollToFirstFilteredRow(map, filteredRows, recentRowNumber);
            });
    }

    scrollToFirstFilteredRow(
        map: GreenHouseMap,
        filteredRows: number[],
        recentRowNumber: number | undefined,
    ): void {
        if (!map.centralPathRowSets.length) return;
        let index = 0;

        if (recentRowNumber) {
            index = this.getScrollToValue(map, recentRowNumber);
        }
        if (filteredRows.length) {
            index = this.getScrollToValue(map, filteredRows[0]);
        }
        // Currently, scrollToIndex will display the selected row first in the list in the UI,
        // In order to show previous rows for ease of user selection, we will display 2 rows before the selected row
        this.viewport?.scrollToIndex(index - 2, 'smooth');
    }

    getScrollToValue(map: GreenHouseMap, rowNumber: number): number {
        // Check if row number exist in given greenhouse map, if not return 0 which set default scroll to first item in the list
        let scrollToValue = 0;
        const foundIndex = map.centralPathRowSets.findIndex(
            (r) => r.left?.number === rowNumber || r.right?.number === rowNumber,
        );
        if (foundIndex !== -1) scrollToValue = foundIndex;
        return scrollToValue;
    }
}
